// i18n Translation Service
// Dynamic runtime locale support with backward compatibility
import { SupportedLocale, TranslationKeys, AllTranslationKeys } from './types'
import { dateLocaleTag } from './date-locale'

// Import all translation files
import csTranslations from './cs.json'
import enTranslations from './en.json'
import deTranslations from './de.json'
import frTranslations from './fr.json'
import itTranslations from './it.json'
import nlTranslations from './nl.json'
import plTranslations from './pl.json'
import ptTranslations from './pt.json'
import esTranslations from './es.json'

// Export translations for runtime usage
export const translations: Record<SupportedLocale, TranslationKeys> = {
  cs: csTranslations as unknown as TranslationKeys,
  en: enTranslations as unknown as TranslationKeys,
  de: deTranslations as unknown as TranslationKeys,
  fr: frTranslations as unknown as TranslationKeys,
  it: itTranslations as unknown as TranslationKeys,
  nl: nlTranslations as unknown as TranslationKeys,
  pl: plTranslations as unknown as TranslationKeys,
  pt: ptTranslations as unknown as TranslationKeys,
  es: esTranslations as unknown as TranslationKeys
}

// Re-export context hook as primary API
export { useLocale, LocaleProvider } from './locale-context'

// Re-export cookie utilities
export {
  LOCALE_COOKIE_NAME,
  LOCALE_DEFAULTS,
  getLocaleCookieClient,
  setLocaleCookieClient,
  detectBrowserLocale,
  parseAcceptLanguage,
  parseLocaleCookie,
  getCurrencyForLocale,
  getTimezoneForLocale
} from './locale-cookie'

/**
 * Get translated text by key with nested object support and parameter interpolation
 * Supports dot notation: "chatbots.create_new" or "pricing.tiers.basic.name"
 * Supports parameter interpolation: "expires_in_days" with { days: 5 }
 */
export function getTranslation(locale: SupportedLocale, key: string, params?: Record<string, any>): string {
  const keys = key.split('.')
  let current: any = translations[locale]

  for (const k of keys) {
    if (current && typeof current === 'object' && k in current) {
      current = current[k]
    } else {
      console.warn(`Translation key not found: ${key}`)
      return key // Return the key itself if translation is missing
    }
  }

  let result = typeof current === 'string' ? current : key

  // Parameter interpolation
  if (params && typeof result === 'string') {
    Object.keys(params).forEach(paramKey => {
      const placeholder = `{${paramKey}}`
      result = result.replace(new RegExp(placeholder, 'g'), String(params[paramKey]))
    })
  }

  return result
}

/**
 * Format price with correct currency symbol
 * @param amount - The price amount
 * @param locale - The locale to format for (defaults to 'en')
 */
export function formatPrice(amount: number, locale: SupportedLocale = 'en'): string {
  if (locale === 'cs') {
    return `${amount} Kč`
  } else {
    return `$${amount}`
  }
}

/**
 * Format date according to locale
 * @param date - The date to format
 * @param locale - The locale to format for (defaults to 'en')
 */
export function formatDate(date: Date | string, locale: SupportedLocale = 'en'): string {
  // Supabase returns timestamps without timezone info (e.g. "2026-05-21 05:23:10.350936").
  // Without a Z or offset suffix JS parses them as local time instead of UTC.
  // Normalise: replace space separator with T and append Z if no offset is present.
  const toUtcDate = (s: string): Date => {
    const normalised = s.replace(' ', 'T')
    return new Date(/(Z|[+-]\d{2}(:?\d{2})?)$/.test(normalised) ? normalised : normalised + 'Z')
  }
  const d = typeof date === 'string' ? toUtcDate(date) : date

  if (locale === 'cs') {
    return d.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  return d.toLocaleDateString(dateLocaleTag(locale), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Format currency amounts with proper symbols and formatting
 * @param amount - The amount to format
 * @param locale - The locale to format for (defaults to 'en')
 * @param showDecimals - Whether to show decimal places
 */
export function formatCurrency(amount: number, locale: SupportedLocale = 'en', showDecimals: boolean = false): string {
  if (locale === 'cs') {
    const formatted = showDecimals
      ? amount.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : amount.toLocaleString('cs-CZ')
    return `${formatted} Kč`
  } else {
    const formatted = showDecimals
      ? amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : amount.toLocaleString('en-US')
    return `$${formatted}`
  }
}

/**
 * Get appropriate thousand separator for locale
 * @param locale - The locale (defaults to 'en')
 */
export function getThousandSeparator(locale: SupportedLocale = 'en'): string {
  return locale === 'cs' ? ' ' : ','
}

/**
 * Get appropriate decimal separator for locale
 * @param locale - The locale (defaults to 'en')
 */
export function getDecimalSeparator(locale: SupportedLocale = 'en'): string {
  return locale === 'cs' ? ',' : '.'
}

// Re-export types for convenience
export type { SupportedLocale, TranslationKeys, AllTranslationKeys }
